import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import Filter from "./Filter";
import Customer from "./Customer";
import PopupCustomer from "./PopupCustomer";
import PopupOrders from "./PopupOrders";
import PopupTransactions from "./PopupTransactions";
import PopupPool from "views/pages/Store/TicketRestaurant/PopupPool";
import PopupAdvantage from "views/pages/Store/TicketRestaurant/PopupAdvantage";
import PopupSav from "./PopupSav";
import TicketSavEntry from "./TicketSavEntry";
import { savOperations } from "state/ducks/store/sav";
import swal from "sweetalert";
import PopupPasswordReset from "./PopupPasswordReset";
import { customersOperations } from "state/ducks/store/customers";
import { notificationsOperations } from "state/ducks/notifications";
import {
  ticketRestaurantOperations,
  ticketRestaurantSelector,
} from "state/ducks/store/ticketRestaurant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandPointUp } from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import {
  buildCustomerSearch,
  CUSTOMER_VIEWS,
  parseCustomerDeepLink,
} from "./customerDeepLink";
import useCustomerParentUrlSync from "./useCustomerParentUrlSync";

const Customers = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { customerId: urlCustomerId, vue: urlVue } = parseCustomerDeepLink(
    location.search
  );

  useCustomerParentUrlSync(location.search);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingPopup, setIsLoadingPopup] = useState(false);
  const [isForEditPopup, setIsForEditPopup] = useState(false);

  const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
  const [isLoadingOrdersPopup, setIsLoadingOrdersPopup] = useState(false);

  const [isPoolModalOpen, setIsPoolModalOpen] = useState(false);
  const [isLoadingPoolPopup, setIsLoadingPoolPopup] = useState(false);

  const [isSavModalOpen, setIsSavModalOpen] = useState(false);
  // Ticket Freshdesk collé dans le bloc « Partir d'un ticket », repris par la
  // modale SAV. Oublié à la fermeture de la modale : il ne doit pas resservir
  // pour le client suivant.
  const [pendingTicketId, setPendingTicketId] = useState(null);
  // Nombre de propositions SAV en cours pour la fiche ouverte (badge du bouton).
  const [inProgressSavCount, setInProgressSavCount] = useState(0);

  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);

  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);

  const [isAdvantageModalOpen, setIsAdvantageModalOpen] = useState(false);
  const [isLoadingAdvantagePopup, setIsLoadingAdvantagePopup] = useState(false);
  const [currentCustomerId, setCurrentCustomerId] = useState(null);

  const lastSyncedUrlRef = useRef("");

  const establishment = useSelector(
    (state) => state.session.establishment,
    shallowEqual
  );

  const customers = useSelector((state) => state.store.customers, shallowEqual);

  const customerPool = useSelector((state) =>
    ticketRestaurantSelector.getCustomerPoolSelector(state)
  );

  const customerAdvantage = useSelector((state) =>
    ticketRestaurantSelector.getCustomerAdvantageSelector(state)
  );

  const setCustomerUrl = useCallback(
    (customerId, vue = CUSTOMER_VIEWS.GLOBAL) => {
      const search = buildCustomerSearch(customerId, vue);
      const nextUrl = `${location.pathname}${search}`;
      if (`${location.pathname}${location.search}` !== nextUrl) {
        history.push({ pathname: location.pathname, search });
      }
      lastSyncedUrlRef.current = nextUrl;
    },
    [history, location.pathname, location.search]
  );

  const findCustomerById = useCallback(
    (customerId) =>
      (customers.data || []).find(
        (customer) => String(customer.id) === String(customerId)
      ),
    [customers.data]
  );

  const loadCustomerOrders = useCallback(
    (customer) => {
      setIsOrdersModalOpen(true);
      setIsLoadingOrdersPopup(true);
      return dispatch(customersOperations.getStoreCustomerOrders(customer)).then(
        () => {
          setIsLoadingOrdersPopup(false);
        },
        () => {
          setIsOrdersModalOpen(false);
          setIsLoadingOrdersPopup(false);
        }
      );
    },
    [dispatch]
  );

  const loadCustomerTransactions = useCallback(
    (customerId, days = 90, nocache = false) => {
      setIsTransactionsModalOpen(true);
      // Erreur déjà notifiée par le middleware et stockée dans customers.transactions
      return dispatch(
        customersOperations.getStoreCustomerTransactions(customerId, days, nocache)
      ).catch(() => {});
    },
    [dispatch]
  );

  const loadCustomerPool = useCallback(
    (customerId) => {
      setIsPoolModalOpen(true);
      setIsLoadingPoolPopup(true);
      return dispatch(ticketRestaurantOperations.getCustomerPool(customerId)).then(
        () => {
          setIsLoadingPoolPopup(false);
        },
        () => {
          setIsPoolModalOpen(false);
          setIsLoadingPoolPopup(false);
        }
      );
    },
    [dispatch]
  );

  const loadCustomerAdvantage = useCallback(
    (customerId) => {
      setCurrentCustomerId(customerId);
      setIsAdvantageModalOpen(true);
      setIsLoadingAdvantagePopup(true);
      return dispatch(
        ticketRestaurantOperations.getCustomerAdvantage(customerId)
      ).then(
        () => {
          setIsLoadingAdvantagePopup(false);
        },
        () => {
          setIsAdvantageModalOpen(false);
          setIsLoadingAdvantagePopup(false);
        }
      );
    },
    [dispatch]
  );

  const selectCustomer = useCallback(
    (customer, vue = CUSTOMER_VIEWS.GLOBAL) => {
      dispatch(customersOperations.getStoreCustomerInfo(customer));
      setCustomerUrl(customer.id, vue);
    },
    [dispatch, setCustomerUrl]
  );

  /**
   * Retire la fiche affichée et remet la page dans l'état « aucun client
   * sélectionné » — exactement ce qu'un F5 donnait jusqu'ici.
   */
  const clearCustomer = useCallback(() => {
    dispatch(customersOperations.getStoreCustomerInfo({}));
    setCustomerUrl(null);
    setInProgressSavCount(0);
  }, [dispatch, setCustomerUrl]);

  // Dépend de l'id seul, jamais de l'objet client : sa référence change à chaque
  // mise à jour du store, ce qui relancerait le chargement en boucle.
  const ficheCustomerId = customers.customer?.id;
  const refreshInProgressSav = useCallback(() => {
    if (!ficheCustomerId) {
      setInProgressSavCount(0);
      return;
    }
    dispatch(savOperations.listCustomerPropositions(ficheCustomerId)).then(
      (res) => setInProgressSavCount((res?.data?.result?.items || []).length),
      () => setInProgressSavCount(0)
    );
  }, [dispatch, ficheCustomerId]);

  useEffect(() => {
    refreshInProgressSav();
  }, [refreshInProgressSav]);

  /**
   * SAV depuis un ticket : le client est celui dont le compte porte l'email du
   * ticket. On ouvre sa fiche puis la modale, qui récupère le ticket d'elle-même
   * (catégorie, motif, commande vérifiée). Sans compte correspondant — le client
   * écrit souvent depuis une autre adresse — le ticket reste en attente et sera
   * repris quand l'agent aura ouvert la bonne fiche.
   */
  const startSavFromTicket = useCallback(
    async (ticketId, item) => {
      setPendingTicketId(ticketId);
      // La fiche ouverte est celle du ticket PRÉCÉDENT : on la retire avant même
      // de résoudre celui-ci. Sans ça, un ticket dont l'email ne correspond à
      // aucun compte laissait la fiche d'avant à l'écran, et « Faire un SAV »
      // écrivait le nouveau ticket sur le client précédent — cagnotte à la
      // mauvaise personne (retour d'Arthur, 29/10/2026).
      clearCustomer();

      const ticketCustomerId = item?.customer?.id;
      if (!ticketCustomerId) {
        swal({
          title: "Client à retrouver",
          text: `Aucun compte client ne porte l'email du ticket (${item?.contactEmail || "inconnu"}). `
            + `C'est fréquent : le client écrit souvent depuis une autre adresse. Recherchez sa fiche, `
            + `le ticket #${ticketId} sera repris quand vous cliquerez « Faire un SAV ».`,
          icon: "info",
        });
        return;
      }

      let customer = findCustomerById(ticketCustomerId);
      if (!customer) {
        // Absent de la liste chargée (autre établissement…) : on charge sa fiche.
        // `result` est l'enveloppe { customer }, pas le client : en lisant `result`
        // directement on récupérait un objet sans `id`, jamais nul, donc le garde-fou
        // ci-dessous ne se déclenchait jamais.
        try {
          const res = await dispatch(customersOperations.getStoreCustomerDetail(ticketCustomerId));
          customer = res?.data?.result?.customer || null;
        } catch (e) {
          customer = null;
        }
      }
      if (!customer) {
        swal({
          title: "Fiche client introuvable",
          text: `Le compte #${ticketCustomerId} n'a pas pu être chargé. Recherchez le client : le ticket #${ticketId} sera repris.`,
          icon: "warning",
        });
        return;
      }

      selectCustomer(customer);
      setIsSavModalOpen(true);
    },
    [clearCustomer, dispatch, findCustomerById, selectCustomer]
  );

  const closeSavModal = useCallback(() => {
    setIsSavModalOpen(false);
    setPendingTicketId(null);
    // Une proposition a pu être créée ou annulée dans la fenêtre.
    refreshInProgressSav();
  }, [refreshInProgressSav]);

  const openCustomerOrders = useCallback(
    (customer, e) => {
      e.preventDefault();
      setCustomerUrl(customer.id, CUSTOMER_VIEWS.COMMANDES);
      loadCustomerOrders(customer);
    },
    [loadCustomerOrders, setCustomerUrl]
  );

  const openCustomerTransactions = useCallback(
    (customer, e) => {
      e.preventDefault();
      setCustomerUrl(customer.id, CUSTOMER_VIEWS.TRANSACTIONS);
      loadCustomerTransactions(customer.id);
    },
    [loadCustomerTransactions, setCustomerUrl]
  );

  const openCustomerPool = useCallback(
    (customerId, e) => {
      e.preventDefault();
      setCustomerUrl(customerId, CUSTOMER_VIEWS.PORTE_MONNAIE);
      loadCustomerPool(customerId);
    },
    [loadCustomerPool, setCustomerUrl]
  );

  const openCustomerAdvantage = useCallback(
    (customerId, e) => {
      e.preventDefault();
      setCustomerUrl(customerId, CUSTOMER_VIEWS.AVANTAGES);
      loadCustomerAdvantage(customerId);
    },
    [loadCustomerAdvantage, setCustomerUrl]
  );

  const closeOrdersModal = useCallback(() => {
    setIsOrdersModalOpen(false);
    if (customers.customer?.id) {
      setCustomerUrl(customers.customer.id, CUSTOMER_VIEWS.GLOBAL);
    }
  }, [customers.customer?.id, setCustomerUrl]);

  const closeTransactionsModal = useCallback(() => {
    setIsTransactionsModalOpen(false);
    if (customers.customer?.id) {
      setCustomerUrl(customers.customer.id, CUSTOMER_VIEWS.GLOBAL);
    }
  }, [customers.customer?.id, setCustomerUrl]);

  const closePoolModal = useCallback(() => {
    setIsPoolModalOpen(false);
    if (customers.customer?.id) {
      setCustomerUrl(customers.customer.id, CUSTOMER_VIEWS.GLOBAL);
    }
  }, [customers.customer?.id, setCustomerUrl]);

  const closeAdvantageModal = useCallback(() => {
    setIsAdvantageModalOpen(false);
    if (customers.customer?.id) {
      setCustomerUrl(customers.customer.id, CUSTOMER_VIEWS.GLOBAL);
    }
  }, [customers.customer?.id, setCustomerUrl]);

  useEffect(() => {
    if (establishment) {
      dispatch(customersOperations.initPage(establishment));
    }
  }, [dispatch, establishment]);

  useEffect(() => {
    // On attend que la liste soit chargée. Au tout premier rendu elle est vide
    // et `initPage` n'a pas encore démarré : partir tout de suite marquait l'URL
    // comme traitée, puis le passage à `isLoading` relançait l'effet et annulait
    // le travail en cours. Au retour de la liste, l'URL était déjà marquée et
    // plus rien ne se produisait.
    if (!urlCustomerId || customers.isLoading || !(customers.data || []).length) {
      return;
    }

    const currentUrl = `${location.pathname}${location.search}`;
    if (lastSyncedUrlRef.current === currentUrl) {
      return;
    }

    lastSyncedUrlRef.current = currentUrl;

    let abandoned = false;

    const open = async () => {
      let customer = findCustomerById(urlCustomerId);
      if (!customer) {
        // Absent de la liste chargée — un client de l'autre établissement, par
        // exemple. On chargeait rien et on ne disait rien : le lien semblait
        // mort et la fiche précédente restait affichée.
        try {
          const res = await dispatch(customersOperations.getStoreCustomerDetail(urlCustomerId));
          customer = res?.data?.result?.customer || null;
        } catch (e) {
          customer = null;
        }
      }
      if (abandoned) {
        return;
      }
      if (!customer) {
        swal({
          title: "Fiche client introuvable",
          text: `Le compte #${urlCustomerId} n'a pas pu être chargé. Recherchez le client ci-dessus.`,
          icon: "warning",
        });
        return;
      }

      if (String(customers.customer?.id) !== String(urlCustomerId)) {
        dispatch(customersOperations.getStoreCustomerInfo(customer));
      }

      setIsOrdersModalOpen(false);
      setIsPoolModalOpen(false);
      setIsAdvantageModalOpen(false);
      setIsTransactionsModalOpen(false);

      if (urlVue === CUSTOMER_VIEWS.COMMANDES) {
        loadCustomerOrders(customer);
        return;
      }
      if (urlVue === CUSTOMER_VIEWS.PORTE_MONNAIE) {
        loadCustomerPool(customer.id);
        return;
      }
      if (urlVue === CUSTOMER_VIEWS.AVANTAGES) {
        loadCustomerAdvantage(customer.id);
        return;
      }
      if (urlVue === CUSTOMER_VIEWS.TRANSACTIONS) {
        loadCustomerTransactions(customer.id);
      }
    };

    open();

    return () => {
      abandoned = true;
    };
  }, [
    urlCustomerId,
    urlVue,
    customers.isLoading,
    customers.data,
    customers.customer?.id,
    dispatch,
    findCustomerById,
    loadCustomerAdvantage,
    loadCustomerOrders,
    loadCustomerTransactions,
    loadCustomerPool,
    location.pathname,
    location.search,
  ]);

  const customerHandle = (mode, data) => {
    setIsLoadingPopup(true);
    if (mode === "edit") {
      dispatch(customersOperations.updateStoreCustomer(data)).then(
        (result) => {
          setIsLoadingPopup(false);
          setIsModalOpen(false);
          if (result?.data?.result) {
            dispatch(
              customersOperations.getStoreCustomerInfo(result.data.result)
            );
            setCustomerUrl(result.data.result.id, CUSTOMER_VIEWS.GLOBAL);
          }
        },
        () => {
          setIsLoadingPopup(false);
        }
      );
      return;
    }
    if (mode === "add") {
      dispatch(customersOperations.addStoreCustomer(data)).then(
        (result) => {
          setIsLoadingPopup(false);
          setIsModalOpen(false);
          if (result?.data?.result) {
            dispatch(
              customersOperations.getStoreCustomerInfo(result.data.result)
            );
            setCustomerUrl(result.data.result.id, CUSTOMER_VIEWS.GLOBAL);
          }
        },
        () => {
          setIsLoadingPopup(false);
        }
      );
    }
  };

  const sendSMSHandle = async (customer) => {
    return dispatch(customersOperations.sendCodeBySMS(customer)).then(
      (result) => {
        if (result?.data?.result?.success) {
          return dispatch(
            notificationsOperations.getResponseSuccess("SMS envoyé")
          );
        }
        return dispatch(
          notificationsOperations.getResponseSuccess("Echec de l'envoi")
        );
      }
    );
  };

  return (
    <>
      <div className="text-right mt-3">
        <Button
          onClick={() => {
            setIsModalOpen(true);
            setIsForEditPopup(false);
          }}
          disabled={customers.isLoading}
        >
          Inscrire un client en boutique
        </Button>
      </div>
      <Filter
        isLoadingPage={customers.isLoading}
        customers={customers.data}
        onCustomerSelected={selectCustomer}
      />
      <TicketSavEntry
        disabled={customers.isLoading}
        pendingTicketId={pendingTicketId}
        onTicketResolved={startSavFromTicket}
        onClearPending={() => setPendingTicketId(null)}
      />
      <Customer
        customer={customers.customer}
        establishment={establishment}
        openPopupCustomerHandle={() => {
          setIsModalOpen(true);
          setIsForEditPopup(true);
        }}
        sendSMSHandle={sendSMSHandle}
        onOpenPasswordReset={() => setIsPasswordResetOpen(true)}
        onOpenOrders={openCustomerOrders}
        onOpenPool={openCustomerPool}
        onOpenAdvantage={openCustomerAdvantage}
        onOpenSav={() => setIsSavModalOpen(true)}
        inProgressSavCount={inProgressSavCount}
        onOpenTransactions={openCustomerTransactions}
      />
      {!customers.customer.id && (
        <Explain>
          <p>
            <FontAwesomeIcon icon={faHandPointUp} size="lg" />
            <br />
            Lancer une recherche ci-dessus pour avoir les détails sur un client
            dont ses commandes.
          </p>
        </Explain>
      )}

      <PopupCustomer
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        onEscapeKeyDown={() => setIsModalOpen(false)}
        customerHandle={customerHandle}
        isLoading={isLoadingPopup}
        customer={customers.customer}
        isEdit={isForEditPopup}
        customers={customers.data}
      />

      <PopupOrders
        show={isOrdersModalOpen}
        onHide={closeOrdersModal}
        onEscapeKeyDown={closeOrdersModal}
        customer={customers.customer}
        isLoading={isLoadingOrdersPopup}
      />

      <PopupTransactions
        show={isTransactionsModalOpen}
        onHide={closeTransactionsModal}
        onEscapeKeyDown={closeTransactionsModal}
        customer={customers.customer}
        transactions={customers.transactions}
        onChangeDays={(days, nocache) =>
          loadCustomerTransactions(customers.customer.id, days, nocache)
        }
      />

      <PopupPool
        show={isPoolModalOpen}
        onHide={closePoolModal}
        onEscapeKeyDown={closePoolModal}
        customerPool={customerPool}
        isLoading={isLoadingPoolPopup}
      />

      <PopupPasswordReset
        show={isPasswordResetOpen}
        onHide={() => setIsPasswordResetOpen(false)}
        customer={customers.customer}
      />

      <PopupSav
        show={isSavModalOpen}
        onHide={closeSavModal}
        onEscapeKeyDown={closeSavModal}
        customer={customers.customer}
        initialTicketId={pendingTicketId}
      />

      <PopupAdvantage
        show={isAdvantageModalOpen}
        onHide={closeAdvantageModal}
        onEscapeKeyDown={closeAdvantageModal}
        customerAdvantage={customerAdvantage}
        isLoading={isLoadingAdvantagePopup}
        customerId={currentCustomerId}
      />
    </>
  );
};

const Explain = styled.div`
  margin-top: 1.5rem;
  p {
    color: #bbb;
    text-align: center;
    font-size: ${(props) => props.theme.fonts.size.l};
  }
`;

Customers.propTypes = {};

Customers.defaultProps = {};

export default Customers;
